import { useCallback, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

// Recorrido guiado reutilizable (driver.js). Cada vista solo aporta sus pasos:
// { phase, title, text, element?, side?, align?, note?, center?, nextText?, prevText?,
//   sidebar?, submenu?, modal?, requires?, waitFor?, appears?, ...datos propios de la vista }
// waitFor: milisegundos que se espera el elemento (contenido que la vista carga de forma asíncrona).
// Antes de mostrar cada paso se emite TOUR_STEP_EVENT con el paso (null al cerrar):
// las vistas lo escuchan con useGuidedTourStep para abrir submenús, modales, etc.
// Estilos: .encalle-tour en index.css (tokens §9 del manual de marca).

const TOUR_STEP_EVENT = 'guided-tour:step';
const emitStep = (step) => window.dispatchEvent(new CustomEvent(TOUR_STEP_EVENT, { detail: step }));

// Escucha el paso activo del recorrido (null cuando se cierra).
export const useGuidedTourStep = (onStep) => {
  useEffect(() => {
    const listener = (event) => onStep(event.detail);
    window.addEventListener(TOUR_STEP_EVENT, listener);
    return () => window.removeEventListener(TOUR_STEP_EVENT, listener);
  }, [onStep]);
};

// Primer elemento visible: algunas vistas dibujan dos versiones (escritorio / móvil) y una queda oculta.
export const findVisible = (selector) => [...document.querySelectorAll(selector)].find((el) => el.getClientRects().length > 0);

// Un paso se omite si su elemento no está en pantalla (ej.: secciones que el rol no ve). Se conservan
// los que aparecen recién al aplicar el paso: los de un modal y los ítems de un submenú cerrado.
// `requires` reemplaza ese control por otro selector, para elementos que la vista muestra recién al
// aplicar el paso (ej.: las tarjetas del móvil, dentro de un bloque desplegable).
// `appears` lo conserva siempre: la vista dibuja el elemento al aplicar el paso (ej.: la tarea de
// ejemplo del recorrido del técnico).
const isAvailable = (step) => {
  if (step.appears) return true;
  if (step.requires) return Boolean(findVisible(step.requires));
  return !step.element || step.modal || findVisible(step.element)
    || (step.submenu && findVisible(`[data-tour="nav-${step.submenu}"]`));
};

// El aro sobresale 6px del elemento: si un contenedor con overflow lo recortaría, se dibuja hacia adentro.
const RING_INSET = 'encalle-tour-ring-inset';
const isRingClipped = (element) => {
  const rect = element.getBoundingClientRect();
  for (let parent = element.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
    const { overflowX, overflowY } = getComputedStyle(parent);
    if (overflowX === 'visible' && overflowY === 'visible') continue;
    const bounds = parent.getBoundingClientRect();
    if (rect.left - 8 < bounds.left || rect.right + 8 > bounds.right
      || rect.top - 8 < bounds.top || rect.bottom + 8 > bounds.bottom) return true;
  }
  return false;
};

const createEl = (tag, className, text = '') => Object.assign(document.createElement(tag), { className, textContent: text });

// Agrega al popover lo que driver.js no trae: fase, nota, barra de progreso y "Omitir".
const decorate = ({ title, footer, progress }, step, index, total, tour) => {
  if (step.center) title.before(createEl('span', 'encalle-tour-hero'));
  title.before(createEl('span', 'encalle-tour-phase', step.phase));
  if (step.note) footer.before(createEl('p', 'encalle-tour-note', step.note));
  const row = createEl('div', 'encalle-tour-progress');
  const bar = createEl('span', 'encalle-tour-bar');
  bar.style.setProperty('--tour-progress', `${((index + 1) / total) * 100}%`);
  progress.innerHTML = `<b>${index + 1}</b>/${total} pasos`;
  row.append(progress, bar);
  footer.before(row);
  if (step.center || index === total - 1) return;
  const skip = createEl('button', 'encalle-tour-skip', 'Omitir');
  skip.type = 'button';
  skip.onclick = () => tour.destroy();
  footer.prepend(skip);
};

export const useGuidedTour = ({ steps, autoStart = false }) => {
  const tourRef = useRef(null);
  const autoStarted = useRef(false);

  const start = useCallback(() => {
    if (!steps) return;
    tourRef.current?.destroy();
    const visible = steps.filter(isAvailable);
    // Aplica el paso (modal, submenú, menú) antes de que driver.js busque y mida su elemento.
    // Si el elemento queda tapado dentro de un contenedor con scroll (ej.: el cuerpo de un modal),
    // se desplaza ese contenedor: driver.js solo desplaza la ventana.
    const reveal = (step) => {
      const element = step.element && findVisible(step.element);
      element?.scrollIntoView({ block: element.closest('[data-tour-scroll]') ? 'center' : 'nearest' });
    };
    const show = (index) => {
      flushSync(() => emitStep(visible[index]));
      reveal(visible[index]);
      tour.moveTo(index);
    };
    const tour = driver({
      steps: visible.map((step, index) => ({
        element: step.element && (() => findVisible(step.element)),
        waitForElement: step.waitFor,
        data: step,
        popover: {
          title: step.title,
          description: step.text,
          side: step.side,
          align: step.align,
          onPopoverRender: (popover) => decorate(popover, step, index, visible.length, tour),
          ...(step.center && {
            popoverClass: 'encalle-tour encalle-tour-center',
            nextBtnText: step.nextText,
            prevBtnText: step.prevText,
            disableButtons: [],
            onPrevClick: () => tour.destroy(),
          }),
        },
      })),
      showProgress: true,
      nextBtnText: 'Siguiente',
      prevBtnText: 'Atrás',
      doneBtnText: 'Finalizar',
      closeBtnLabel: 'Cerrar recorrido',
      popoverClass: 'encalle-tour',
      overlayColor: 'var(--app-overlay)',
      overlayOpacity: 1,
      stagePadding: 8,
      stageRadius: 12,
      smoothScroll: true,
      allowScroll: false, // el scroll lo maneja el recorrido; ver también index.css
      disableActiveInteraction: true, // sin clics dentro del recuadro (evita navegar a mitad del recorrido)
      // Solo se cierra con ×, Omitir, Ahora no o Finalizar: ni clic afuera ni teclado (Esc).
      overlayClickBehavior: 'none',
      allowKeyboardControl: false,
      onNextClick: (_el, _step, { driver: active }) => {
        const next = active.getActiveIndex() + 1;
        if (next < visible.length) show(next);
        else active.destroy();
      },
      onPrevClick: (_el, _step, { driver: active }) => show(active.getActiveIndex() - 1),
      onHighlighted: (element) => element?.classList.toggle(RING_INSET, isRingClipped(element)),
      onDeselected: (element) => element?.classList.remove(RING_INSET),
      onDestroyed: () => {
        document.querySelectorAll(`.${RING_INSET}`).forEach((element) => element.classList.remove(RING_INSET));
        emitStep(null);
      },
    });
    tourRef.current = tour;
    flushSync(() => emitStep(visible[0]));
    reveal(visible[0]);
    tour.drive();
  }, [steps]);

  // Autoarranque una sola vez por sesión (el llamador decide cuándo, ej.: isFirstLogin).
  // Se programa y se cancela en la limpieza: con StrictMode (montar → desmontar →
  // montar) corre una sola vez, y ya con la vista pintada.
  useEffect(() => {
    if (!autoStart || autoStarted.current) return undefined;
    const timer = setTimeout(() => {
      autoStarted.current = true;
      start();
    });
    return () => clearTimeout(timer);
  }, [autoStart, start]);

  useEffect(() => () => tourRef.current?.destroy(), []);

  return { start, hasTour: Boolean(steps) };
};
